import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ResultsState, ResultsFilter, Place, SearchStatistics, ResultsSummary } from '../../types/results.types';
import { Search } from '../../types/search.types';
import * as resultsApi from '../../api/results';

const initialState: ResultsState = {
  places: [],
  summary: null,
  pagination: {
    page: 1,
    totalPages: 0,
    itemsPerPage: 20,
    totalItems: 0,
  },
  filters: {},
  statistics: null,
  verifiable_emails_count: 0,
  enrichable_people_count: 0,
  feature_access: null,
  current_plan: null,
  auto_find_status: null,
  auto_find_mode: null,
  auto_find_success_limit: 0,
  auto_find_success_count: 0,
  auto_find_chargeable_count: 0,
  auto_find_attempt_count: 0,
  auto_find_no_email_count: 0,
  auto_find_catch_all_count: 0,
  auto_find_guardrail_reason: null,
  auto_find_business_target: 0,
  auto_find_business_attempt_count: 0,
  auto_find_max_people_per_business: 0,
  auto_find_monthly_pool_limit: 0,
  auto_find_monthly_pool_used: 0,
  loading: false,
  error: null,
};

// Helper function to format error
const formatError = (error: any): string => {
  if (typeof error === 'string') return error;
  if (error?.response?.data?.detail) {
    return typeof error.response.data.detail === 'string' 
      ? error.response.data.detail 
      : JSON.stringify(error.response.data.detail);
  }
  return 'An unexpected error occurred';
};

export const fetchResults = createAsyncThunk(
  'results/fetchResults',
  async ({ 
    searchId, 
    page = 1, 
    limit = 20, 
    filters 
  }: { 
    searchId: number;
    page?: number;
    limit?: number;
    filters?: ResultsFilter;
    silent?: boolean;
  }, { rejectWithValue }) => {
    try {
      return await resultsApi.getResults(searchId, page, limit, filters);
    } catch (error: any) {
      return rejectWithValue(formatError(error));
    }
  }
);

export const fetchStatistics = createAsyncThunk(
  'results/fetchStatistics',
  async (searchId: number, { rejectWithValue }) => {
    try {
      return await resultsApi.getStatistics(searchId);
    } catch (error: any) {
      return rejectWithValue(formatError(error));
    }
  }
);

export const exportResultsThunk = createAsyncThunk(
  'results/exportResults',
  async ({
    searchId,
    format = 'csv',
    coverage = 'ready',
    includeSharedInboxes = false,
    filters: filtersOverride,
  }: {
    searchId: number;
    format?: 'csv' | 'excel';
    coverage?: 'ready' | 'expanded';
    includeSharedInboxes?: boolean;
    filters?: ResultsFilter;
  }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { results: ResultsState; search: { currentSearch: Search | null } };
      const filters = filtersOverride || state.results.filters;
      const currentSearch = state.search.currentSearch;

      const blob = await resultsApi.exportResults(
        searchId,
        format,
        filters,
        coverage,
        includeSharedInboxes,
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fileExtension = format === 'excel' ? 'xlsx' : 'csv';
      
      // Create filename with search query and city name if available
      let filename = `search-results-${searchId}`;
      if (currentSearch && currentSearch.search_query && currentSearch.city_name) {
        const sanitizedQuery = currentSearch.search_query.replace(/[^a-zA-Z0-9]/g, '_');
        const sanitizedCityName = currentSearch.city_name.replace(/[^a-zA-Z0-9]/g, '_');
        filename = `${sanitizedQuery}_${sanitizedCityName}_results`;
      }
      
      a.download = `${filename}.${fileExtension}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      return true;
    } catch (error: any) {
      return rejectWithValue(formatError(error));
    }
  }
);

const resultsSlice = createSlice({
  name: 'results',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<ResultsFilter>) => {
      state.filters = action.payload;
    },
    changePage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    clearResults: (state) => {
      state.places = [];
      state.summary = null;
      state.pagination = {
        page: 1,
        totalPages: 0,
        itemsPerPage: 20,
        totalItems: 0,
      };
      state.filters = {};
      state.statistics = null;
      state.verifiable_emails_count = 0;
      state.enrichable_people_count = 0;
      state.feature_access = null;
      state.current_plan = null;
      state.auto_find_status = null;
      state.auto_find_mode = null;
      state.auto_find_success_limit = 0;
      state.auto_find_success_count = 0;
      state.auto_find_chargeable_count = 0;
      state.auto_find_attempt_count = 0;
      state.auto_find_no_email_count = 0;
      state.auto_find_catch_all_count = 0;
      state.auto_find_guardrail_reason = null;
      state.auto_find_business_target = 0;
      state.auto_find_business_attempt_count = 0;
      state.auto_find_max_people_per_business = 0;
      state.auto_find_monthly_pool_limit = 0;
      state.auto_find_monthly_pool_used = 0;
    },
    patchEmailResults: (
      state,
      action: PayloadAction<{ email_id: number; email_type: 'person' | 'place'; status: string; verified_at?: string }>
    ) => {
      const { email_id, email_type, status, verified_at } = action.payload;
      for (const place of state.places) {
        if (email_type === 'place' && place.company_emails) {
          const email = place.company_emails.find((e) => e.id === email_id);
          if (email) {
            (email as any).verification_status = status;
            if (verified_at) (email as any).verified_at = verified_at;
          }
        }
        if (email_type === 'person' && place.people) {
          for (const person of place.people) {
            const email = person.emails?.find((e) => e.id === email_id);
            if (email) {
              (email as any).verification_status = status;
              if (verified_at) (email as any).verified_at = verified_at;
            }
          }
        }
      }
    },
    patchEmailResultsBatch: (
      state,
      action: PayloadAction<Array<{ email_id: number; email_type: 'person' | 'place'; status: string; verified_at?: string }>>
    ) => {
      if (action.payload.length === 0) return;
      const placePatches = new Map<number, { status: string; verified_at?: string }>();
      const personPatches = new Map<number, { status: string; verified_at?: string }>();
      for (const p of action.payload) {
        (p.email_type === 'place' ? placePatches : personPatches).set(p.email_id, {
          status: p.status,
          verified_at: p.verified_at,
        });
      }
      for (const place of state.places) {
        if (placePatches.size > 0 && place.company_emails) {
          for (const email of place.company_emails) {
            if (!email.id) continue;
            const patch = placePatches.get(email.id);
            if (patch) {
              (email as any).verification_status = patch.status;
              if (patch.verified_at) (email as any).verified_at = patch.verified_at;
            }
          }
        }
        if (personPatches.size > 0 && place.people) {
          for (const person of place.people) {
            if (!person.emails) continue;
            for (const email of person.emails) {
              if (!email.id) continue;
              const patch = personPatches.get(email.id);
              if (patch) {
                (email as any).verification_status = patch.status;
                if (patch.verified_at) (email as any).verified_at = patch.verified_at;
              }
            }
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch results
      .addCase(fetchResults.pending, (state, action) => {
        if (!action.meta.arg.silent) {
          state.loading = true;
          state.error = null;
        }
      })
      .addCase(fetchResults.fulfilled, (state, action) => {
        state.loading = false;
        state.places = action.payload.results;
        state.pagination.totalPages = action.payload.pages;
        state.pagination.totalItems = action.payload.total;
        state.verifiable_emails_count = action.payload.verifiable_emails_count || 0;
        state.enrichable_people_count = action.payload.enrichable_people_count || 0;
        state.feature_access = action.payload.feature_access || null;
        state.current_plan = action.payload.current_plan || null;
        state.summary = action.payload.summary || null;
        state.auto_find_status = action.payload.auto_find_status || null;
        state.auto_find_mode = action.payload.auto_find_mode || null;
        state.auto_find_success_limit = action.payload.auto_find_success_limit || 0;
        state.auto_find_success_count = action.payload.auto_find_success_count || 0;
        state.auto_find_chargeable_count = action.payload.auto_find_chargeable_count || 0;
        state.auto_find_attempt_count = action.payload.auto_find_attempt_count || 0;
        state.auto_find_no_email_count = action.payload.auto_find_no_email_count || 0;
        state.auto_find_catch_all_count = action.payload.auto_find_catch_all_count || 0;
        state.auto_find_guardrail_reason = action.payload.auto_find_guardrail_reason || null;
        state.auto_find_business_target = action.payload.auto_find_business_target || 0;
        state.auto_find_business_attempt_count = action.payload.auto_find_business_attempt_count || 0;
        state.auto_find_max_people_per_business = action.payload.auto_find_max_people_per_business || 0;
        state.auto_find_monthly_pool_limit = action.payload.auto_find_monthly_pool_limit || 0;
        state.auto_find_monthly_pool_used = action.payload.auto_find_monthly_pool_used || 0;
        if (action.payload.pagination_full) {
          state.pagination.places_locked = action.payload.pagination_full.places_locked || 0;
          state.pagination.total_places_found = action.payload.pagination_full.total_places_found || 0;
          state.pagination.credits_charged = action.payload.pagination_full.credits_charged || 0;
          state.pagination.has_locked_results = action.payload.pagination_full.has_locked_results || false;
          state.pagination.all_locked = action.payload.pagination_full.all_locked || false;
          state.pagination.results_capped = action.payload.pagination_full.results_capped || false;
          state.pagination.total_links_found = action.payload.pagination_full.total_links_found || 0;
          state.pagination.visible_results_cap = action.payload.pagination_full.visible_results_cap ?? null;
        }
      })
      .addCase(fetchResults.rejected, (state, action) => {
        if (!action.meta.arg.silent) {
          state.loading = false;
          state.error = action.payload as string;
        }
      })
      
      // Export results
      .addCase(exportResultsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportResultsThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportResultsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch statistics
      .addCase(fetchStatistics.fulfilled, (state, action: PayloadAction<SearchStatistics>) => {
        state.statistics = action.payload;
      });
  },
});

export const { clearError, setFilters, changePage, clearResults, patchEmailResults, patchEmailResultsBatch } = resultsSlice.actions;
export default resultsSlice.reducer; 
